import { Injectable } from '@angular/core';
import {combineLatestWith, Observable, of} from 'rxjs';
import { map } from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import {asyncActionToBoolean} from '@emm/core/util/ngrx.util';
import {AttributesStoreService} from '@emm/management/store/attributes/attributes-store.service';
import {ManagementBaseStoreService} from '@emm/management/store/base/management-base-store.service';
import {OperatorLocksStoreService} from '@emm/management/store/emergency-items/operator-locks-store.service';
import {ItemGroupingsStoreService} from '@emm/management/store/item-groupings/item-groupings-store.service';
import {ItemsStoreService} from '@emm/menu/store/items/items-store.service';
import {ItemTypesStoreService} from '@emm/management/store/item-types/item-types-store.service';
import {ItemTypeClassesStoreService} from '@emm/management/store/item-type-classes/item-type-classes-store.service';
import {ManagementLocationSetsStoreService} from '@emm/management/store/location-sets/management-location-sets-store.service';
import {ManagementLocationSetTypesStoreService} from '@emm/management/store/location-set-types/management-location-set-types-store.service';
import {MasterMenuSystemStoreService} from '@emm/management/store/mms-revote/master-menu-system.store.service';
import {MmsAdminOverrideStoreService} from '@emm/management/store/mms-admin-override/mms-admin-override-store.service';
import {NutritionStoreService} from '@emm/management/store/nutrition/nutrition-store.service';
import {
    LanguageAssignmentsStoreService
} from '@emm/management/store/language-assignments/language-assignments.store.service';
import {LocationSetsAction} from '@emm/management/store/location-sets/location-sets.actions';
import {ManagementBaseAction} from '@emm/management/store/base/management-base.actions';
import {OperatorLocksAction} from '@emm/management/store/emergency-items/operator-locks.actions';
import {ItemGroupingsAction} from '@emm/management/store/item-groupings/item-groupings.actions';
import {
    ManagementConsumerTypesStoreService
} from '@emm/management/store/consumer-types/management-consumer-types-store.service';

@Injectable({ providedIn: 'root' })
export class ManagementStoreService {

    queryAttributes = this.attributesStore.queries;
    queryBase = this.baseStore.queries;
    queryConsumerTypes = this.consumerTypesStore.queries;
    queryEmergencyItems = this.emergencyItemsStore.queries;
    queryItemGroupings = this.itemGroupingsStore.queries;
    queryItems = this.itemsStore.queries;
    queryItemTypeClasses = this.itemTypeClassesStore.queries;
    queryItemTypes = this.itemTypesStore.queries;
    queryLocationSets = this.locationSetsStore.queries;
    queryLocationSetTypes = this.locationSetTypesStore.queries;
    queryMasterMenu = this.masterMenuStore.queries;
    queryMmsAdminOverride = this.mmsAdminOverrideStore.queries;
    queryNutrition = this.nutritionStore.queries;
    queryLanguageAssignments = this.lagnuageAssignmentsStore.queries;

    dispatch = {
        ...this.attributesStore.dispatch,
        ...this.baseStore.dispatch,
        ...this.consumerTypesStore.dispatch,
        ...this.emergencyItemsStore.dispatch,
        ...this.itemGroupingsStore.dispatch,
        ...this.itemsStore.dispatch,
        ...this.itemTypeClassesStore.dispatch,
        ...this.itemTypesStore.dispatch,
        ...this.locationSetsStore.dispatch,
        ...this.locationSetTypesStore.dispatch,
        ...this.masterMenuStore.dispatch,
        ...this.mmsAdminOverrideStore.dispatch,
        ...this.nutritionStore.dispatch,
        ...this.lagnuageAssignmentsStore.dispatch,
    };

    isLoading$ = {
        attributes: this.queryAttributes.isLoading$,
        base: this.queryBase.isLoading$,
        consumerTypes: this.queryConsumerTypes.isLoading$,
        emergencyItems: this.queryEmergencyItems.isLoading$,
        itemGroupings: this.queryItemGroupings.isLoading$,
        items: this.queryItems.isLoading$,
        itemTypeClasses: this.queryItemTypeClasses.isLoading$,
        itemTypes: this.queryItemTypes.isLoading$,
        locationSets: this.queryLocationSets.isLoading$,
        locationSetTypes: this.queryLocationSetTypes.isLoading$,
        masterMenu: this.queryMasterMenu.isLoading$,
        mmsAdminOverride: this.queryMmsAdminOverride.isLoading$,
        nutrition: this.queryNutrition.isLoading$,
        languageAssignments: this.queryLanguageAssignments.isLoading$,
    };

    anyLoading$ = of([]).pipe(combineLatestWith([
            this.isLoading$.attributes,
            this.isLoading$.base,
            this.isLoading$.consumerTypes,
            this.isLoading$.emergencyItems,
            this.isLoading$.itemGroupings,
            this.isLoading$.itemTypeClasses,
            this.isLoading$.itemTypes,
            this.isLoading$.items,
            this.isLoading$.masterMenu,
            this.isLoading$.locationSetTypes,
            this.isLoading$.locationSets,
            this.isLoading$.mmsAdminOverride,
            this.isLoading$.nutrition,
            this.isLoading$.languageAssignments,
        ]),
        map((sources: any[]) => R.any(R.equals(true), sources))
    );

    updateItem$ = asyncActionToBoolean(
        this.queryBase,
        ManagementBaseAction.UpdateItemSuccess,
        ManagementBaseAction.UpdateItemFailure
    );

    updateItemType$ = asyncActionToBoolean(
        this.queryBase,
        ManagementBaseAction.UpdateItemTypeSuccess,
        ManagementBaseAction.UpdateItemTypeFailure
    );

    updateLocationSetType$ = asyncActionToBoolean(
        this.queryBase,
        ManagementBaseAction.UpdateLocationSetTypeSuccess,
        ManagementBaseAction.UpdateLocationSetTypeFailure
    );

    updateRevote$ = asyncActionToBoolean(
        this.queryBase,
        ManagementBaseAction.UpdateRevoteSuccess,
        ManagementBaseAction.UpdateRevoteFailure
    );

    addChildItem$ = asyncActionToBoolean(
        this.queryEmergencyItems,
        OperatorLocksAction.AddChildItemSuccess,
        OperatorLocksAction.AddChildItemFailure
    );

    removeChildItem$ = asyncActionToBoolean(
        this.queryEmergencyItems,
        OperatorLocksAction.RemoveChildItemSuccess,
        OperatorLocksAction.RemoveChildItemFailure
    );

    updateStatusMessage$ = asyncActionToBoolean(
        this.queryEmergencyItems,
        OperatorLocksAction.UpdateStatusMessageSuccess,
        OperatorLocksAction.UpdateStatusMessageFailure
    );

    updateItemGrouping$ = asyncActionToBoolean(
        this.queryBase,
        ManagementBaseAction.UpdateItemGroupingSuccess,
        ManagementBaseAction.UpdateItemGroupingFailure
    );

    toggleItemGroupingDisabled$ = asyncActionToBoolean(
        this.queryItemGroupings,
        ItemGroupingsAction.ToggleDisabledSuccess,
        ItemGroupingsAction.ToggleDisabledFailure
    );

    addItemGroupChildItem$ = asyncActionToBoolean(
        this.queryItemGroupings,
        ItemGroupingsAction.AddChildItemSuccess,
        ItemGroupingsAction.AddChildItemFailure
    );

    removeItemGroupChildItem$ = asyncActionToBoolean(
        this.queryItemGroupings,
        ItemGroupingsAction.RemoveChildItemSuccess,
        ItemGroupingsAction.RemoveChildItemFailure
    );

    deleteItemGroup$ = asyncActionToBoolean(
        this.queryItemGroupings,
        ItemGroupingsAction.DeleteItemGroupSuccess,
        ItemGroupingsAction.DeleteItemGroupFailure
    );

    updateLocationSet$ = asyncActionToBoolean(
        this.queryBase,
        ManagementBaseAction.UpdateLocationSetSuccess,
        ManagementBaseAction.UpdateLocationSetFailure
    );

    addChildLocations$ = asyncActionToBoolean(
        this.queryLocationSets,
        LocationSetsAction.AddChildLocationsSuccess,
        LocationSetsAction.AddChildLocationsFailure
    );

    deleteLocationSet$ = asyncActionToBoolean(
        this.queryLocationSets,
        LocationSetsAction.DeleteLocationSetSuccess,
        LocationSetsAction.DeleteLocationSetFailure
    );

    removeChildLocation$ = asyncActionToBoolean(
        this.queryLocationSets,
        LocationSetsAction.RemoveChildLocationSuccess,
        LocationSetsAction.RemoveChildLocationFailure
    );

    constructor(
        private attributesStore: AttributesStoreService,
        private baseStore: ManagementBaseStoreService,
        private consumerTypesStore: ManagementConsumerTypesStoreService,
        private emergencyItemsStore: OperatorLocksStoreService,
        private itemGroupingsStore: ItemGroupingsStoreService,
        private itemsStore: ItemsStoreService,
        private itemTypesStore: ItemTypesStoreService,
        private itemTypeClassesStore: ItemTypeClassesStoreService,
        private locationSetsStore: ManagementLocationSetsStoreService,
        private locationSetTypesStore: ManagementLocationSetTypesStoreService,
        private masterMenuStore: MasterMenuSystemStoreService,
        private mmsAdminOverrideStore: MmsAdminOverrideStoreService,
        private nutritionStore: NutritionStoreService,
        private lagnuageAssignmentsStore: LanguageAssignmentsStoreService
    ) {}

}
